import { forwardRef, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { cn } from 'Utils/cn';
import { cva } from 'class-variance-authority';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';

const buttonVariants = cva(
    'inline-flex items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50 ring-primary/20 focus-visible:border-primary transition-all [&_svg]:pointer-events-none [&_svg]:shrink-0 shrink-0 disabled:cursor-not-allowed',
    {
        variants: {
            variant: {
                default: 'bg-white text-dark-blue border border-gray-200 shadow-sm hover:bg-gray-50',
                primary: 'bg-dark-blue text-white shadow-sm hover:bg-dark-blue/80',
                ghost: 'text-dark-blue/50 hover:text-dark-blue focus-visible:text-dark-blue focus-visible:border focus-visible:border-primary',
            },
            size: {
                default: 'size-11',
                small: 'size-8',
            },
            pressed: {
                true: '',
                false: '',
            },
        },
        compoundVariants: [
            { variant: 'default', pressed: true, className: 'bg-gray-100 border-gray-300' },
            { variant: 'primary', pressed: true, className: 'bg-dark-blue/70' },
            { variant: 'ghost', pressed: true, className: 'text-dark-blue bg-gray-100' },
        ],
        defaultVariants: {
            variant: 'default',
            size: 'default',
            pressed: false,
        },
    },
);

const IconButton = forwardRef(function IconButton(props, ref) {
    const { className, variant, size, pressed, tooltip, icon, type = 'button', iconSize = 20, testId, ...rest } = props;

    const buttonContent = (
        <button
            aria-label={tooltip}
            aria-pressed={pressed}
            type={type}
            className={cn(buttonVariants({ variant, size, pressed, className }))}
            ref={ref}
            data-testid={testId}
            {...rest}
        >
            {cloneElement(icon, {
                size: iconSize,
                color: 'currentColor',
            })}
        </button>
    );

    if (tooltip != null) {
        return <Tooltip label={tooltip}>{buttonContent}</Tooltip>;
    }

    return buttonContent;
});

export default IconButton;

IconButton.propTypes = {
    className: PropTypes.string,
    variant: PropTypes.oneOf(['default', 'primary', 'ghost']),
    size: PropTypes.oneOf(['default', 'small']),
    pressed: PropTypes.bool,
    disabled: PropTypes.bool,
    tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    name: PropTypes.string,
    iconSize: PropTypes.number,
    icon: PropTypes.node,
    type: PropTypes.oneOf(['button', 'submit', 'reset']),
    testId: PropTypes.string,
};
